import type { AssetId } from '@/shared/types/core'

export const GSPLAT_PICK_CLEAR_ID = 0xffffffff
export const GSPLAT_MAX_PICK_RECORD_ID = 0xfffffffe
const GSPLAT_MAX_PICK_RECORD_COUNT = GSPLAT_PICK_CLEAR_ID

export interface GSplatPickRecord {
  readonly assetId: AssetId
  readonly placementId: number
  readonly sourceResourceId: string | number
  readonly originalSplatIndex: number
  readonly assetOrder: number
}

export interface GSplatPickSourceRange {
  readonly assetId: AssetId
  readonly placementId: number
  readonly sourceResourceId: string | number
  readonly originalSplatStart: number
  readonly count: number
  readonly assetOrder: number
}

export interface GSplatPickWorkBufferSource {
  readonly assetId?: AssetId
  readonly placementId: number
  readonly sourceResourceId: string | number
  readonly originalSplatStart?: number
  readonly count?: number
  readonly activeSplats?: number
  readonly intervals?: readonly number[]
  readonly intervalOffsets?: readonly number[]
  readonly assetOrder: number
}

interface GSplatPickRecordRange extends GSplatPickSourceRange {
  readonly recordStart: number
}

interface GSplatPickWorkBufferRange extends GSplatPickRecordRange {
  readonly workBufferStart: number
}

function validateUint32(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0 || value > 0xffffffff) {
    throw new Error(`非法 ${label}: ${value}`)
  }
}

function freezeRecord(record: GSplatPickRecord): GSplatPickRecord {
  validateUint32(record.originalSplatIndex, 'originalSplatIndex')
  if (!Number.isSafeInteger(record.placementId) || record.placementId < 0) {
    throw new Error(`非法 placementId: ${record.placementId}`)
  }
  if (!Number.isSafeInteger(record.assetOrder) || record.assetOrder < 0) {
    throw new Error(`非法 assetOrder: ${record.assetOrder}`)
  }
  return Object.freeze({ ...record })
}

/**
 * WorldState-owned 的不可变 pickRecordId → original identity 快照。
 *
 * @remarks
 * 当前已验证的 WebGPU Hybrid `direct` 路径仍输出 WorkBuffer ID，并由 WorldState interval layout
 * 还原 original splat；它不能被直接当作这里的 pickRecordId。正式接入本索引时，ID raster、
 * padding/clear 过滤与结果解码必须在同一个 WorldState generation 内一起切换。
 */
export class GSplatPickIndex {
  private static readonly EMPTY = new GSplatPickIndex(Object.freeze([]), Object.freeze([]), 0)

  private constructor(
    private readonly records: readonly GSplatPickRecord[],
    private readonly recordRanges: readonly GSplatPickRecordRange[],
    private readonly recordCount: number,
    private readonly workBufferRanges: readonly GSplatPickWorkBufferRange[] = Object.freeze([])
  ) {}

  static empty(): GSplatPickIndex {
    return GSplatPickIndex.EMPTY
  }

  static fromRecords(records: readonly GSplatPickRecord[]): GSplatPickIndex {
    if (records.length > GSPLAT_MAX_PICK_RECORD_COUNT) {
      throw new Error(`GSplatPickIndex record 数超出 RGBA8 ID 空间: ${records.length}`)
    }
    const frozenRecords = Object.freeze(records.map(freezeRecord))
    return new GSplatPickIndex(frozenRecords, Object.freeze([]), frozenRecords.length)
  }

  /**
   * 为已经完成 WorkBuffer layout 的 WorldState 建立 immutable identity snapshot。
   * record id 按 placement/interval/source 的提交顺序分配；WorkBuffer offset 只作为
   * 迁移期 shader 输出的 alias，永远不会成为对外 original splat identity。
   */
  static fromWorkBufferSources(sources: readonly GSplatPickWorkBufferSource[]): GSplatPickIndex {
    const recordRanges: GSplatPickRecordRange[] = []
    const workBufferRanges: GSplatPickWorkBufferRange[] = []
    let recordCount = 0
    const append = (
      source: GSplatPickWorkBufferSource,
      offset: number,
      originalStart: number,
      count: number
    ): void => {
      if (!Number.isSafeInteger(source.placementId) || source.placementId < 0) {
        throw new Error(`非法 placementId: ${source.placementId}`)
      }
      if (!Number.isSafeInteger(source.assetOrder) || source.assetOrder < 0) {
        throw new Error(`非法 assetOrder: ${source.assetOrder}`)
      }
      if (!Number.isSafeInteger(offset) || offset < 0) {
        throw new Error(`非法 GSplat WorkBuffer offset: ${offset}`)
      }
      if (!Number.isSafeInteger(originalStart) || originalStart < 0) {
        throw new Error(`非法 originalSplatStart: ${originalStart}`)
      }
      if (!Number.isSafeInteger(count) || count < 0) {
        throw new Error(`非法 GSplat pick range count: ${count}`)
      }
      if (count > 0 && originalStart + count - 1 > 0xffffffff) {
        throw new Error('originalSplatIndex 超出 uint32')
      }
      if (offset + count - 1 > 0xffffffff) {
        throw new Error('GSplat WorkBuffer ID 超出 uint32')
      }
      if (recordCount + count > GSPLAT_MAX_PICK_RECORD_COUNT) {
        throw new Error(`GSplatPickIndex record 数超出 RGBA8 ID 空间: ${recordCount + count}`)
      }
      if (count === 0) return
      const range = Object.freeze({
        assetId: source.assetId ?? String(source.sourceResourceId),
        placementId: source.placementId,
        sourceResourceId: source.sourceResourceId,
        originalSplatStart: originalStart,
        count,
        assetOrder: source.assetOrder,
        recordStart: recordCount,
        workBufferStart: offset,
      })
      recordRanges.push(range)
      workBufferRanges.push(range)
      recordCount += count
    }

    for (const source of sources) {
      const intervals = source.intervals ?? []
      const offsets = source.intervalOffsets ?? []
      if (intervals.length % 2 !== 0) throw new Error('GSplat intervals 必须为 start/end 对')
      if (intervals.length > 0) {
        if (offsets.length !== intervals.length / 2) {
          throw new Error('GSplat interval offsets 数量不匹配')
        }
        for (let index = 0; index < offsets.length; index++) {
          const start = intervals[index * 2]
          const end = intervals[index * 2 + 1]
          if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || end < start) {
            throw new Error(`非法 GSplat interval: ${start},${end}`)
          }
          append(source, offsets[index], start, end - start)
        }
      } else {
        append(
          source,
          offsets[0] ?? 0,
          source.originalSplatStart ?? 0,
          source.count ?? source.activeSplats ?? 0
        )
      }
    }
    workBufferRanges.sort((left, right) => left.workBufferStart - right.workBufferStart)
    for (let index = 1; index < workBufferRanges.length; index++) {
      const previous = workBufferRanges[index - 1]
      const current = workBufferRanges[index]
      if (current.workBufferStart < previous.workBufferStart + previous.count) {
        throw new Error(`GSplat WorkBuffer ID 重复: ${current.workBufferStart}`)
      }
    }
    return new GSplatPickIndex(
      Object.freeze([]),
      Object.freeze(recordRanges),
      recordCount,
      Object.freeze(workBufferRanges)
    )
  }

  static fromRanges(ranges: readonly GSplatPickSourceRange[]): GSplatPickIndex {
    let total = 0
    for (const range of ranges) {
      validateUint32(range.originalSplatStart, 'originalSplatStart')
      if (!Number.isSafeInteger(range.count) || range.count < 0) {
        throw new Error(`非法 GSplat pick range count: ${range.count}`)
      }
      total += range.count
      if (total > GSPLAT_MAX_PICK_RECORD_COUNT) {
        throw new Error(`GSplatPickIndex record 数超出 RGBA8 ID 空间: ${total}`)
      }
      if (range.count > 0) {
        validateUint32(range.originalSplatStart + range.count - 1, 'originalSplatIndex')
      }
    }

    const recordRanges: GSplatPickRecordRange[] = []
    let recordStart = 0
    for (const range of ranges) {
      if (range.count === 0) continue
      if (!Number.isSafeInteger(range.placementId) || range.placementId < 0) {
        throw new Error(`非法 placementId: ${range.placementId}`)
      }
      if (!Number.isSafeInteger(range.assetOrder) || range.assetOrder < 0) {
        throw new Error(`非法 assetOrder: ${range.assetOrder}`)
      }
      recordRanges.push(Object.freeze({ ...range, recordStart }))
      recordStart += range.count
    }
    return new GSplatPickIndex(Object.freeze([]), Object.freeze(recordRanges), total)
  }

  get size(): number {
    return this.recordCount
  }

  get(pickRecordId: number): GSplatPickRecord | undefined {
    if (!Number.isSafeInteger(pickRecordId) || pickRecordId < 0) return undefined
    const direct = this.records[pickRecordId]
    if (direct) return direct
    const range = this.findRecordRange(pickRecordId)
    if (!range) return undefined
    return freezeRecord({
      assetId: range.assetId,
      placementId: range.placementId,
      sourceResourceId: range.sourceResourceId,
      originalSplatIndex: range.originalSplatStart + pickRecordId - range.recordStart,
      assetOrder: range.assetOrder,
    })
  }

  getRecordIdForWorkBufferId(workBufferId: number): number | undefined {
    if (!Number.isSafeInteger(workBufferId) || workBufferId < 0) return undefined
    let low = 0
    let high = this.workBufferRanges.length - 1
    while (low <= high) {
      const middle = (low + high) >>> 1
      const range = this.workBufferRanges[middle]
      if (workBufferId < range.workBufferStart) {
        high = middle - 1
      } else if (workBufferId >= range.workBufferStart + range.count) {
        low = middle + 1
      } else {
        return range.recordStart + workBufferId - range.workBufferStart
      }
    }
    return undefined
  }

  getByWorkBufferId(workBufferId: number): GSplatPickRecord | undefined {
    const recordId = this.getRecordIdForWorkBufferId(workBufferId)
    return recordId === undefined ? undefined : this.get(recordId)
  }

  encodeLittleEndian(pickRecordId: number, target = new Uint8Array(4)): Uint8Array {
    if (target.byteLength < 4) throw new Error('GSplat pick ID target 至少需要 4 bytes')
    if (
      !Number.isSafeInteger(pickRecordId) ||
      pickRecordId < 0 ||
      pickRecordId > GSPLAT_MAX_PICK_RECORD_ID ||
      pickRecordId >= this.recordCount
    ) {
      throw new Error(`无效 GSplat pickRecordId: ${pickRecordId}`)
    }
    target[0] = pickRecordId & 0xff
    target[1] = (pickRecordId >>> 8) & 0xff
    target[2] = (pickRecordId >>> 16) & 0xff
    target[3] = (pickRecordId >>> 24) & 0xff
    return target
  }

  static decodeLittleEndian(bytes: ArrayLike<number>, offset = 0): number {
    if (!Number.isSafeInteger(offset) || offset < 0 || offset + 4 > bytes.length) {
      throw new Error(`非法 GSplat pick ID byte offset: ${offset}`)
    }
    return (
      (bytes[offset] |
        (bytes[offset + 1] << 8) |
        (bytes[offset + 2] << 16) |
        (bytes[offset + 3] << 24)) >>>
      0
    )
  }

  private findRecordRange(pickRecordId: number): GSplatPickRecordRange | undefined {
    let low = 0
    let high = this.recordRanges.length - 1
    while (low <= high) {
      const middle = (low + high) >>> 1
      const range = this.recordRanges[middle]
      if (pickRecordId < range.recordStart) {
        high = middle - 1
      } else if (pickRecordId >= range.recordStart + range.count) {
        low = middle + 1
      } else {
        return range
      }
    }
    return undefined
  }
}
